import { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { sidcSvg } from '../../utils/military'

// Military plan overlay (cartes d'état-major): symbol/label DOM markers +
// line/polygon/arrow geometries, plus click capture for the active drawing tool.
// milplanTick re-runs the render once the style is ready — shared plans
// (?milplan=) load before the map style and used to silently never render their
// lines/zones (only the DOM arrow heads showed).
export function useMilPlan(map, { milPlanFeatures, milPlanActive, onMilPlanClick }) {
  const milPlanMarkersRef = useRef([])
  const [milplanTick, setMilplanTick] = useState(0)

  useEffect(() => {
    if (!map.current) return
    milPlanMarkersRef.current.forEach((m) => m.remove())
    milPlanMarkersRef.current = []

    const parseStyle = (f) => {
      try { return { ...JSON.parse(f.style || '{}') } } catch { return {} }
    }
    const widthPx = (s) => (s.width === 'thin' ? 2 : s.width === 'thick' ? 6 : 3.5)
    const dashOf = (s) => (s.dash === 'dash' ? 'dash' : s.dash === 'dot' ? 'dot' : 'solid')

    const features = milPlanFeatures || []
    const lineFeatures = []
    features.forEach((f) => {
      let coords
      try { coords = JSON.parse(f.geometry) } catch { return }
      const st = parseStyle(f)
      if (f.feature_type === 'marker' || f.feature_type === 'label') {
        const [lon, lat] = Array.isArray(coords[0]) ? coords[0] : coords
        const el = document.createElement('div')
        if (f.feature_type === 'marker') {
          el.innerHTML = sidcSvg(f.sidc || '10031000001211000000', st.size || 32)
          if (f.label) {
            el.title = f.label
            const caption = document.createElement('div')
            caption.textContent = f.label
            caption.style.cssText = 'font-size:10px;color:#fff;text-shadow:0 1px 3px #000;text-align:center;white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis'
            el.appendChild(caption)
          }
        } else {
          el.textContent = f.label || ''
          el.style.cssText = `color:${f.color || '#fff'};font-weight:700;font-size:${st.size || 13}px;text-shadow:0 1px 3px #000;white-space:nowrap`
        }
        el.dataset.milplanFeature = f.id ?? ''
        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lon, lat])
          .addTo(map.current)
        milPlanMarkersRef.current.push(marker)
      } else {
        lineFeatures.push({
          type: 'Feature',
          geometry: {
            type: f.feature_type === 'polygon' ? 'Polygon' : 'LineString',
            coordinates: f.feature_type === 'polygon' ? [coords] : coords,
          },
          properties: {
            color: f.color || '#e07070',
            kind: f.feature_type,
            width: widthPx(st),
            dash: dashOf(st),
          },
        })
        // arrow heads: rotated DOM markers (the style has no glyph source,
        // symbol text layers can't render). heads: 0 | 1 (end) | 2 (both).
        if (f.feature_type === 'arrow' && coords.length >= 2) {
          const heads = st.heads === 0 ? 0 : (st.heads === 2 ? 2 : 1)
          const addHead = (from, to) => {
            const bearing = (Math.atan2(to[0] - from[0], to[1] - from[1]) * 180) / Math.PI
            const el = document.createElement('div')
            el.textContent = '▲'
            el.style.cssText = `color:${f.color || '#e07070'};font-size:${14 + widthPx(st) * 2}px;line-height:1;text-shadow:0 1px 2px #000;pointer-events:none`
            const head = new maplibregl.Marker({
              element: el, anchor: 'center',
              rotation: bearing, rotationAlignment: 'map', pitchAlignment: 'map',
            }).setLngLat(to).addTo(map.current)
            milPlanMarkersRef.current.push(head)
          }
          if (heads >= 1) addHead(coords[coords.length - 2], coords[coords.length - 1])
          if (heads === 2) addHead(coords[1], coords[0])
        }
      }
    })

    const fc = { type: 'FeatureCollection', features: lineFeatures }
    const src = map.current.getSource('milplan-lines')
    if (!src) {
      if (!map.current.getLayer('regions-fill')) {
        // style not ready yet — retry once it is (fixes shared plans)
        map.current.once('idle', () => setMilplanTick((t) => t + 1))
        return
      }
      map.current.addSource('milplan-lines', { type: 'geojson', data: fc })
      // dasharray can't be data-driven → one line layer per dash style
      const lineLayer = (id, dash, dasharray) => {
        const paint = {
          'line-color': ['get', 'color'],
          'line-width': ['coalesce', ['get', 'width'], 3.5],
        }
        if (dasharray) paint['line-dasharray'] = dasharray
        map.current.addLayer({
          id,
          type: 'line',
          source: 'milplan-lines',
          filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'dash'], dash]],
          paint,
        })
      }
      lineLayer('milplan-lines-solid', 'solid', null)
      lineLayer('milplan-lines-dash', 'dash', [2.2, 1.4])
      lineLayer('milplan-lines-dot', 'dot', [0.4, 1.6])
      map.current.addLayer({
        id: 'milplan-polygons',
        type: 'fill',
        source: 'milplan-lines',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.18 },
      })
      map.current.addLayer({
        id: 'milplan-polygons-outline',
        type: 'line',
        source: 'milplan-lines',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['coalesce', ['get', 'width'], 2],
        },
      })
    } else {
      src.setData(fc)
    }
    return () => {
      milPlanMarkersRef.current.forEach((m) => m.remove())
      milPlanMarkersRef.current = []
    }
  }, [map, milPlanFeatures, milplanTick])

  // Plan edit mode: capture raw map clicks for the active drawing tool.
  useEffect(() => {
    if (!map.current || !milPlanActive || !onMilPlanClick) return
    const handler = (e) => onMilPlanClick({ lng: e.lngLat.lng, lat: e.lngLat.lat })
    map.current.on('click', handler)
    map.current.getCanvas().style.cursor = 'crosshair'
    return () => {
      map.current.off('click', handler)
      map.current.getCanvas().style.cursor = ''
    }
  }, [map, milPlanActive, onMilPlanClick])
}
